import { useMemo, useEffect, useState } from "react";
import * as nursery from "@/lib/nurseryEngine";
import { useApp } from "@/context/AppCtx";
import { useWatchdog } from "./useWatchdog";

export type StrategyRecommendation = {
  id: string;
  type: "momentum" | "revival" | "scalp" | "whale";
  label: string;
  coin: string;
  mint: string;
  name?: string; // Added full name field
  desc: string;
  confidence: number;
  bondingPct: number;
  whaleSol: number;
  marketCapSol?: number; // Market cap in SOL for image sizing
  imageUri?: string | null;
  isDead: boolean;
  isPumping: boolean;
  isRugged: boolean;
  ruggedAtMs?: number;
  creator?: string;
  phantomTrade?: {
    entryMcSol: number;
    exitMcSol?: number;
    pnlPct?: number;
    status: "open" | "won" | "lost";
  };
  evidenceTrades?: any[]; // The trades that triggered this signal
  tape?: any[]; // Full price history for sparkline
  primaryWallet?: string;
  primaryWalletEntryCost?: number;
};

export function useStrategist() {
  const { strategistThresholds } = useApp() as any;
  const watchdog = useWatchdog();
  const [snapshot, setSnapshot] = useState(nursery.getSnapshot());

  useEffect(() => {
    const timer = setInterval(() => {
      setSnapshot(nursery.getSnapshot());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const recommendations = useMemo(() => {
    const recs: StrategyRecommendation[] = [];
    const GRAD_SOL = 79;

    // Helper to map token to rec
    const mapToken = (token: any, type: string, label: string, desc: string, confidence: number): StrategyRecommendation => ({
      id: `${type}-${token.mint}`,
      type: "momentum",
      label,
      coin: token.symbol,
      mint: token.mint,
      name: token.name,
      bondingPct: Math.round(Math.min(100, ((token.marketCapSol || token.peakMcSol || 0) / GRAD_SOL) * 100)),
      whaleSol: token.maxSingleBuySol,
      marketCapSol: token.marketCapSol,
      imageUri: token.imageUri,
      desc,
      confidence,
      isDead: !!token.isDead,
      isPumping: token.vol5mSol > 1.5,
      isRugged: !!token.isRugged,
      ruggedAtMs: token.ruggedAtMs,
      creator: token.creator,
      phantomTrade: token.phantomTrade,
      evidenceTrades: token.recentLargeTrades,
      tape: token.tape,
      primaryWallet: token.recentLargeTrades?.[0]?.user,
      primaryWalletEntryCost: token.recentLargeTrades?.[0]?.sol,
    });

    // Strategy 1: The Graduation Scalp (90%+)
    snapshot.visibleTokens.forEach((token) => {
      const pct = Math.min(100, (token.marketCapSol / GRAD_SOL) * 100);
      if (pct >= strategistThresholds.minBonding && token.maxSingleBuySol >= strategistThresholds.minWhaleEntrySol) {
        recs.push(mapToken(
          token, 
          "grad", 
          "Bonding Momentum", 
          `🐋${token.maxSingleBuySol.toFixed(1)} SOL Whale entry + ${Math.round(pct)}% Bonding. Final stretch scalp.`,
          Math.min(99, Math.round(pct + (token.maxSingleBuySol * 5)))
        ));
      }
    });

    // Strategy 2: The New Whale Catalyst (Early detection)
    snapshot.visibleTokens.slice(0, 20).forEach((token) => {
      const pct = Math.min(100, (token.marketCapSol / GRAD_SOL) * 100);
      if (pct < 60 && token.maxSingleBuySol >= 2.0) {
        recs.push(mapToken(
          token,
          "whale",
          "Whale Discovery",
          `🐋${token.maxSingleBuySol.toFixed(1)} SOL entry at only ${Math.round(pct)}% bonding. Early runner candidate.`,
          75
        ));
      }
    });

    // Strategy 3: The Micro-cap Sniper ($3k - $10k MC Runners)
    snapshot.visibleTokens.forEach((t: any) => {
      const pct = Math.min(100, (t.marketCapSol / GRAD_SOL) * 100);
      
      // 1. Momentum Signal (Requires back-to-back buys)
      if (t.momentumBurst && t.buys5m > t.sells5m) {
        recs.push({
          id: `mom-${t.mint}`,
          type: "scalp",
          label: "New Momentum",
          coin: t.symbol,
          mint: t.mint,
          bondingPct: Math.round(Math.min(100, ((t.marketCapSol || 0) / GRAD_SOL) * 100)),
          whaleSol: t.maxSingleBuySol,
          marketCapSol: t.marketCapSol,
          confidence: Math.min(60 + (t.buys5m * 2), 85),
          desc: `Rapid buy momentum detected (${t.buys5m} buys in 5m). Back-to-back activity confirmed.`,
          evidenceTrades: t.tape?.filter((tr: any) => tr.isBuy).slice(0, 5),
          phantomTrade: t.phantomTrade,
          isDead: !!t.isDead,
          isPumping: true,
          isRugged: !!t.isRugged,
          creator: t.creator,
          primaryWallet: t.tape?.filter((tr: any) => tr.isBuy)?.[0]?.user,
          primaryWalletEntryCost: t.tape?.filter((tr: any) => tr.isBuy)?.[0]?.sol
        });
      }

      // 2. Whale Signal (Highest conviction)
      if (t.whaleCount >= strategistThresholds.minWhaleCount && t.momentumBurst) {
        recs.push({
          id: `whale-${t.mint}`,
          type: "whale",
          label: "Whale Cluster",
          coin: t.symbol,
          mint: t.mint,
          bondingPct: Math.round(Math.min(100, ((t.marketCapSol || 0) / GRAD_SOL) * 100)),
          whaleSol: t.maxSingleBuySol,
          marketCapSol: t.marketCapSol,
          confidence: Math.min(85 + (t.whaleCount * 2), 99),
          desc: `${t.whaleCount} unique whale wallets identified. Coordinated entry in progress.`,
          evidenceTrades: t.recentLargeTrades,
          phantomTrade: t.phantomTrade,
          isDead: !!t.isDead,
          isPumping: true,
          isRugged: !!t.isRugged,
          creator: t.creator,
          primaryWallet: t.recentLargeTrades?.[0]?.user,
          primaryWalletEntryCost: t.recentLargeTrades?.[0]?.sol
        });
      }

      // 3. Heavy Upward Buy Pressure
      if (t.buys5m > 0 && t.buys5m >= t.sells5m * 2.5 && t.vol5mSol > 2) {
        recs.push({
          id: `pressure-${t.mint}`,
          type: "momentum",
          label: "Heavy Buy Pressure",
          coin: t.symbol,
          mint: t.mint,
          bondingPct: Math.round(Math.min(100, ((t.marketCapSol || 0) / GRAD_SOL) * 100)),
          whaleSol: t.maxSingleBuySol,
          marketCapSol: t.marketCapSol,
          confidence: Math.min(80 + (t.consecutiveBuys * 3) + t.vol5mSol, 99),
          desc: `Extreme upward buy pressure detected. ${t.buys5m} buys vs ${t.sells5m} sells. ${t.consecutiveBuys} consecutive buys.`,
          evidenceTrades: t.tape?.filter((tr: any) => tr.isBuy).slice(0, 5),
          phantomTrade: t.phantomTrade,
          isDead: !!t.isDead,
          isPumping: true,
          isRugged: !!t.isRugged,
          creator: t.creator
        });
      }

      if (pct >= 15 && pct <= 40 && t.maxSingleBuySol >= strategistThresholds.minWhaleEntrySol) {
        const currentMcUsd = Math.round((t.marketCapSol || 20) * 178.5);
        const targetMultiplier = pct < 25 ? 3 : 2;
        const targetMcUsd = Math.round(currentMcUsd * targetMultiplier);
        const targetSol = Math.round((t.marketCapSol || 20) * targetMultiplier);
        recs.push(mapToken(
          t,
          "momentum",
          "Micro-cap Sniper",
          `🎯Spawn signal: ${t.maxSingleBuySol.toFixed(1)} SOL catalyst at ${Math.round(pct)}% bonding. Dynamic Target: ~${targetSol} SOL ($${targetMcUsd.toLocaleString()}) breakout.`,
          Math.min(95, 70 + (t.maxSingleBuySol * 10))
        ));
      }
    });

    // Strategy 4: Watchdog Revivals
    watchdog.revivals.forEach(r => {
      // Find full token metadata from snapshot if possible to get image, bonding pct, etc.
      const token = snapshot.visibleTokens.find(t => t.mint === r.mint) || snapshot.visibleTokens.find(t => t.mint === r.mint);
      const mcSol = token ? (token as any).marketCapSol : (r.mcUsd ? r.mcUsd / 150 : 0);
      const bondingPct = Math.round(Math.min(100, (mcSol / GRAD_SOL) * 100));

      recs.push({
        id: `revival-${r.mint}`,
        type: "revival",
        label: "Watchdog Revival",
        coin: r.symbol || token?.symbol || r.mint.slice(0, 4),
        mint: r.mint,
        name: token?.name,
        bondingPct: bondingPct,
        whaleSol: 0,
        marketCapSol: mcSol,
        imageUri: token?.imageUri || null,
        confidence: 90, // Revivals are high confidence
        desc: `Bundle sold off + near launch MC ($${Math.round(r.mcUsd || 0)}). Top 1 holder: ${r.top1?.toFixed(1)}%. Alpha detected.`,
        isDead: false,
        isPumping: true,
        isRugged: false,
        creator: token?.creator
      });
    });

    // Filter out dead coins from the matrix immediately, unless they were rugged (keep for countdown)
    return recs
      .filter(r => r.isRugged || !r.isDead)
      .filter(r => r.confidence >= strategistThresholds.minConfidence)
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, 9);
  }, [snapshot, strategistThresholds, watchdog.revivals]);

  return { recommendations, snapshot };
}
